import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { isCafeInactive } from '../utils/coffeeShop';

const CartContext = createContext();

const CART_STORAGE_KEY = 'quickly_coffee_cart';

// Функция для загрузки корзины из localStorage
const defaultState = {
  carts: {},
  activeCafeId: null,
  currentCafe: null
};

const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const parsed = JSON.parse(savedCart);

      if (parsed && typeof parsed === 'object') {
        // Новая структура (после рефакторинга)
        if (parsed.carts) {
          return {
            carts: parsed.carts || {},
            activeCafeId: parsed.activeCafeId ?? null,
            currentCafe: parsed.currentCafe ?? null
          };
        }

        // Старый формат — мигрируем данные
        if (Array.isArray(parsed.items) && parsed.items.length > 0 && parsed.selectedCafe) {
          const cafe = parsed.selectedCafe;
          return {
            carts: {
              [cafe.id]: {
                cafe,
                items: parsed.items
              }
            },
            activeCafeId: cafe.id,
            currentCafe: cafe
          };
        }
      }
    }
  } catch (error) {
    console.error('Ошибка при загрузке корзины из localStorage:', error);
  }

  return { ...defaultState };
};

// Функция для сохранения корзины в localStorage
const saveCartToStorage = (state) => {
  try {
    const dataToSave = {
      carts: state.carts,
      activeCafeId: state.activeCafeId,
      currentCafe: state.currentCafe
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Ошибка при сохранении корзины в localStorage:', error);
  }
};

const removeCafeFromState = (state, cafeId) => {
  if (!state.carts[cafeId]) {
    return state;
  }

  const { [cafeId]: _removed, ...restCarts } = state.carts;
  const remainingCafeIds = Object.keys(restCarts);
  const nextActiveCafeId = state.activeCafeId === cafeId
    ? (remainingCafeIds[0] ?? null)
    : state.activeCafeId;

  return {
    ...state,
    carts: restCarts,
    activeCafeId: nextActiveCafeId
  };
};

const cartReducer = (state, action) => {
  let newState;
  
  switch (action.type) {
    case 'ADD_ITEM':
      {
        const { cafe, item } = action.payload;
        if (!cafe || !cafe.id) {
          console.warn('Попытка добавить товар без информации о кофейне', cafe, item);
          return state;
        }

        const cart = state.carts[cafe.id] || { cafe, items: [] };

        const existingItemIndex = cart.items.findIndex(existingItem => 
          existingItem.id === item.id &&
          existingItem.size === item.size &&
          JSON.stringify(existingItem.customizations) === JSON.stringify(item.customizations)
        );

        let updatedItems;

        if (existingItemIndex >= 0) {
          updatedItems = cart.items.map((existingItem, index) =>
            index === existingItemIndex
              ? { ...existingItem, quantity: existingItem.quantity + 1 }
              : existingItem
          );
        } else {
          updatedItems = [...cart.items, { ...item, quantity: item.quantity ?? 1 }];
        }

        newState = {
          ...state,
          carts: {
            ...state.carts,
            [cafe.id]: {
              cafe: { ...cart.cafe, ...cafe },
              items: updatedItems
            }
          },
          activeCafeId: cafe.id
        };
        break;
      }

    case 'REMOVE_ITEM':
      {
        const { cafeId, index } = action.payload;
        const cart = state.carts[cafeId];

        if (!cart) {
          return state;
        }

        const updatedItems = cart.items.filter((_, itemIndex) => itemIndex !== index);

        if (updatedItems.length === 0) {
          newState = removeCafeFromState(state, cafeId);
        } else {
          newState = {
            ...state,
            carts: {
              ...state.carts,
              [cafeId]: {
                ...cart,
                items: updatedItems
              }
            }
          };
        }
        break;
      }

    case 'UPDATE_QUANTITY':
      {
        const { cafeId, index, quantity } = action.payload;
        const cart = state.carts[cafeId];

        if (!cart) {
          return state;
        }

        if (quantity <= 0) {
          const updatedItems = cart.items.filter((_, itemIndex) => itemIndex !== index);
          if (updatedItems.length === 0) {
            return removeCafeFromState(state, cafeId);
          }

          return {
            ...state,
            carts: {
              ...state.carts,
              [cafeId]: {
                ...cart,
                items: updatedItems
              }
            }
          };
        }

        const updatedItems = cart.items.map((item, itemIndex) =>
          itemIndex === index ? { ...item, quantity } : item
        );

        newState = {
          ...state,
          carts: {
            ...state.carts,
            [cafeId]: {
              ...cart,
              items: updatedItems
            }
          }
        };
        break;
      }

    case 'UPDATE_ITEM':
      {
        const { cafeId, index, updatedItem } = action.payload;
        const cart = state.carts[cafeId];

        if (!cart || index < 0 || index >= cart.items.length) {
          return state;
        }

        const updatedItems = cart.items.map((item, itemIndex) =>
          itemIndex === index ? { ...updatedItem, quantity: updatedItem.quantity ?? item.quantity } : item
        );

        newState = {
          ...state,
          carts: {
            ...state.carts,
            [cafeId]: {
              ...cart,
              items: updatedItems
            }
          }
        };
        break;
      }

    case 'CLEAR_CART':
      newState = { ...defaultState };
      break;

    case 'CLEAR_CAFE':
      newState = removeCafeFromState(state, action.payload);
      break;

    case 'SET_ACTIVE_CAFE':
      newState = {
        ...state,
        activeCafeId: action.payload
      };
      break;

    case 'SET_SELECTED_CAFE': {
      const nextState = {
        ...state,
        currentCafe: action.payload || null
      };

      const payloadCafeId = action.payload?.id;
      if (payloadCafeId && state.carts[payloadCafeId]) {
        nextState.activeCafeId = payloadCafeId;
      }

      newState = nextState;
      break;
    }

    case 'SET_CAFE': {
      const nextState = {
        ...state,
        currentCafe: action.payload || null
      };

      const payloadCafeId = action.payload?.id;
      if (payloadCafeId && state.carts[payloadCafeId]) {
        nextState.activeCafeId = payloadCafeId;
      }

      newState = nextState;
      break;
    }

    default:
      return state;
  }

  return newState;
};

// Инициализируем состояние из localStorage
const initialState = loadCartFromStorage();

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Сохраняем корзину в localStorage при каждом изменении состояния
  useEffect(() => {
    saveCartToStorage(state);
  }, [state]);

  const addItem = (cafe, item) => {
    if (isCafeInactive(cafe)) {
      return;
    }
    dispatch({ type: 'ADD_ITEM', payload: { cafe, item } });
  };

  const removeItem = (cafeId, index) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { cafeId, index } });
  };

  const updateQuantity = (cafeId, index, quantity) => {
    if (quantity <= 0) {
      removeItem(cafeId, index);
      return;
    }
    dispatch({ type: 'UPDATE_QUANTITY', payload: { cafeId, index, quantity } });
  };

  const updateItem = (cafeId, index, updatedItem) => {
    dispatch({ type: 'UPDATE_ITEM', payload: { cafeId, index, updatedItem } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    // Также очищаем localStorage
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const clearCafe = (cafeId) => {
    dispatch({ type: 'CLEAR_CAFE', payload: cafeId });
  };

  const setActiveCafe = (cafeId) => {
    dispatch({ type: 'SET_ACTIVE_CAFE', payload: cafeId });
  };

  const setSelectedCafe = (cafe) => {
    dispatch({ type: 'SET_SELECTED_CAFE', payload: cafe });
  };

  const getItemsForCafe = (cafeId) => {
    if (!cafeId) return [];
    return state.carts[cafeId]?.items || [];
  };

  const getCafeById = (cafeId) => state.carts[cafeId]?.cafe || null;

  const getTotalPrice = (cafeId) => {
    const id = cafeId ?? state.activeCafeId;
    return getItemsForCafe(id).reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = (cafeId) => {
    const id = cafeId ?? state.activeCafeId;
    return getItemsForCafe(id).reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    carts: state.carts,
    activeCafeId: state.activeCafeId,
    selectedCafe: state.currentCafe,
    addItem,
    removeItem,
    updateQuantity,
    updateItem,
    clearCafe,
    clearCart,
    setActiveCafe,
    setSelectedCafe,
    getItemsForCafe,
    getCafeById,
    getTotalPrice,
    getTotalItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
